/** @jsxImportSource @emotion/react */
import { Fragment } from 'react';

import { Wrapped } from '../../hocs/withRenderSettings';
import { Accordion } from '../../modules/Accordion';
import { Anchor } from '../../modules/Anchor';
import { Articles } from '../../modules/Articles';
import { Asset } from '../../modules/Asset';
import { Container } from '../../modules/Container';
import { Form } from '../../modules/Form';
import { Formi } from '../../modules/Formi';
import { Grid } from '../../modules/Grid';
import { ModuleButton } from '../../modules/ModuleButton';
import { sectionsMap } from '../../routes/Page/components/Sections';
import { Storyblok } from '../../storyblok';
import { Html } from '../Html';
import { Markdown } from '../Markdown';
import { Navigation } from '../Navigation';

interface ModuleComponentsProps {
    components?: {
        _uid: string;
        component: string;
        renderSettings?: Storyblok.Component.RenderSettings;
    }[];
}

/**
 * Map of `Flexible Components` which are some kind of "global components" that
 * can be used in many parts of the application
 * @see https://app.storyblok.com/#!/me/spaces/65514/components
 */
const componentsMap = {
    Anchor,
    Container: Container,
    Markdown: Markdown,
    Html: Html,
    Navigation: Navigation,
    Button: ModuleButton,
    Grid: Grid,
    Asset: Asset,
    Form: Form,
    Articles: Articles,
    Formi: Formi,
    Accordion,
} as Record<string, React.VFC<any>>;

/**
 *
 * We are destructuring component's `_uid, rednerSettingsm component` to make
 * sure that these won't be passed down to a target component as a props.
 * The `...rest` should contain only a target component specific props, I.e for
 * `Markdown` component that would be `content` prop.
 */
export const ModuleComponents = ({
    components,
}: ModuleComponentsProps): JSX.Element => {
    return (
        <Fragment>
            {components?.map(
                ({
                    _uid,
                    renderSettings,
                    component,
                    ...storyblookSchemaFieldsData
                }) => {
                    const RenderElement =
                        componentsMap[component] ||
                        sectionsMap[component] ||
                        null;

                    if (!RenderElement) {
                        console.warn(
                            `RenderElement with Storyblok schema name: "${component}" 
                        cannot be rendered as one of ModuleComponents.`,
                        );
                        return null;
                    }

                    /**
                     * `storyblokCustomStyles, storyblokCustomProps, _uid` will be
                     * passed directly to `RenderElement`, those values won't be
                     * available to access via props in `RenderElement`.
                     *
                     * Only values passed as `rest` will be present in props.
                     * Rest should contain every field that is declared in Storyblok
                     * module schema.
                     */
                    return (
                        <Wrapped
                            renderAs={RenderElement}
                            renderSettings={renderSettings}
                            {...storyblookSchemaFieldsData}
                            key={_uid}
                        />
                    );
                    // return (
                    //     <WithRenderSettings
                    //         renderSettings={renderSettings}
                    //         key={_uid}
                    //     >
                    //         <RenderElement {...storyblookSchemaFieldsData} />
                    //     </WithRenderSettings>
                    // );
                },
            )}
        </Fragment>
    );
};
